import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { healthsherpa } from './api';
import { personName, usePersonColors } from './HealthSherpaPerson';
import { bandStyle, dueWords } from './hsBands';
import { displayName } from './hsLabels';
import { fs } from './hsScale';   // 5.31.0: font sizes scale with the page's Text size
import { ComparePanel, CoverageSummary, Flags, MoreDetails, YearTabs, usd, useYearView } from './HsCoverage';   // 5.46.0
import { RenewalBonusPanel } from './HsRenewalBonus';   // 5.47.0

// =============================================================================
// HealthSherpa work (5.30.0, Henry 2026-10-29): claims ("My Clients"), the per-client workspace (notes left, to-do
// right, the client's follow-up alerts + Finalize below), the Follow-ups board and the Finalized list, plus the
// owner box the Clients profile panel uses and the admin alert-types card (Settings).
// Our own database only (/api/healthsherpa/work/*) — nothing here calls HealthSherpa.
// Address bar: the tab plus person / season / filters (Back works); the SEARCH TEXT never goes into the address bar or
// storage (component state only, sent in a POST body — the 5.27.4 rule).
// Privacy: the same switch as the Clients tab (insuredocs.hs.privacy.v1) blurs client names, InsureDocs client names
// and alert notes in every list here (.hs-pii, no hover titles).
// Notes: saved with the conflict guard (expected_updated_at -> 409 when someone saved first); on a 409 the typed
// text is kept on screen and stays after "Load latest" in a box to copy from.
// =============================================================================

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
const work = () => healthsherpa.work;

// --- address bar --------------------------------------------------------------------------------------------------
export const WORK_URL_KEYS = { mine: ['person', 'season'], followups: ['scope', 'type', 'show'],
  finalized: ['season', 'owner', 'from', 'to', 'page'] };
export const WORK_URL_EVENT = 'insuredocs-hs-work-url';

export function readWorkUrl(tab) {
  const out = {};
  try {
    const u = new URLSearchParams(window.location.search);
    (WORK_URL_KEYS[tab] || []).forEach((k) => { const v = u.get(k); if (v) out[k] = v; });
  } catch (_e) { /* no URL access */ }
  return out;
}

export function writeWorkUrl(tab, next, { replace = false } = {}) {
  try {
    const u = new URLSearchParams();
    u.set('healthsherpa', '1');
    u.set('tab', tab);
    (WORK_URL_KEYS[tab] || []).forEach((k) => {
      const v = next[k];
      if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
    });
    const url = `${window.location.pathname}?${u.toString()}`;
    if (url !== `${window.location.pathname}${window.location.search}`) {
      if (replace) window.history.replaceState(null, '', url); else window.history.pushState(null, '', url);
    }
    window.dispatchEvent(new Event(WORK_URL_EVENT));
  } catch (_e) { /* history unavailable */ }
}

function useWorkUrl(tab) {
  const [params, setParams] = useState(() => readWorkUrl(tab));
  useEffect(() => {
    const sync = () => setParams(readWorkUrl(tab));
    window.addEventListener('popstate', sync);
    window.addEventListener(WORK_URL_EVENT, sync);
    return () => { window.removeEventListener('popstate', sync); window.removeEventListener(WORK_URL_EVENT, sync); };
  }, [tab]);
  const go = useCallback((patch, opts) => writeWorkUrl(tab, { ...readWorkUrl(tab), ...patch }, opts), [tab]);
  return [params, go];
}

// --- privacy (shared with the Clients tab) ------------------------------------------------------------------------
const PRIVACY_KEY = 'insuredocs.hs.privacy.v1';
function usePrivacy() {
  const [on, setOn] = useState(() => { try { return window.localStorage.getItem(PRIVACY_KEY) === '1'; } catch (_e) { return false; } });
  const toggle = () => {
    const v = !on;
    setOn(v);
    try { window.localStorage.setItem(PRIVACY_KEY, v ? '1' : '0'); } catch (_e) { /* storage unavailable */ }
  };
  return [on, toggle];
}

// --- meta (users, alert types, seasons) ---------------------------------------------------------------------------
function useMeta(reloadKey = 0) {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    work().meta().then((r) => { if (alive) setMeta(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.status === 403 ? "HealthSherpa Clients isn't open to staff yet." : detailOf(e)); });
    return () => { alive = false; };
  }, [reloadKey]);
  return [meta, error];
}

// --- small UI bits ---------------------------------------------------------------------------------------------------
const css = `
.hs-wk table { border-collapse: collapse; width: 100%; }
.hs-wk th { text-align: left; font-size: calc(12px * var(--hs-scale, 1)); font-weight: 600; padding: 6px 8px; border-bottom: 2px solid var(--wk-border); color: var(--wk-sub); white-space: nowrap; }
.hs-wk td { text-align: left; font-size: calc(13px * var(--hs-scale, 1)); padding: 6px 8px; border-bottom: 1px solid var(--wk-border); vertical-align: top; }
.hs-wk tr.wk-click { cursor: pointer; }
.hs-wk tr.wk-click:hover, .hs-wk tr.wk-click:focus-visible { background: var(--wk-hover); outline: none; }
.hs-wk .hs-pii { filter: blur(5px); user-select: none; }
.hs-wk .wk-sub { color: var(--wk-sub); font-size: calc(12px * var(--hs-scale, 1)); }
`;

const btn = (c, primary, disabled) => ({
  padding: '6px 12px', fontSize: fs(13), borderRadius: 5, cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
  fontWeight: primary ? 600 : 400, opacity: disabled ? 0.55 : 1,
  border: primary ? '1px solid #185FA5' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text,
});
const inputStyle = (c, width) => ({ padding: '6px 8px', fontSize: fs(13), borderRadius: 5, border: `1px solid ${c.border}`, background: c.panel,
  color: c.text, width: width || '100%', boxSizing: 'border-box' });
const box = (c) => ({ background: c.panel, border: `1px solid ${c.border}`, borderRadius: 8, padding: 14 });

function Err({ msg }) {
  if (!msg) return null;
  return <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: fs(13), margin: '8px 0' }}>{msg}</div>;
}

function H({ c, children, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <div style={{ fontSize: fs(12), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub, flex: 1 }}>{children}</div>
      {right}
    </div>
  );
}

export function BandChip({ band, days, completed, testid }) {
  const s = bandStyle(band);
  return (
    <span data-testid={testid} data-band={band} title={s.label}
      style={{ display: 'inline-block', background: s.bg, color: s.text, borderRadius: 10, padding: '1px 9px', fontSize: fs(12), fontWeight: 700, whiteSpace: 'nowrap' }}>
      {band === 'overdue' ? 'OVERDUE' : dueWords(days, completed)}
      {band === 'overdue' ? <span style={{ fontWeight: 400 }}> · {dueWords(days)}</span> : null}
    </span>
  );
}

function PrivacyButton({ c, on, onToggle }) {
  return (
    <button onClick={onToggle} aria-pressed={on} data-testid="wk-privacy" style={{ ...btn(c, on), display: 'inline-flex', alignItems: 'center', gap: 6 }}
      title={on ? 'Privacy is on: client names are blurred — click to show them' : 'Blur client names in the list'}>
      <span aria-hidden="true">👁</span>{on ? 'Privacy on' : 'Privacy'}
    </button>
  );
}

// the search box: state only (never the address bar), debounced, at least 2 characters
function useSearch() {
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const v = text.trim();
    if (v === q || v.length === 1) return undefined;
    const t = setTimeout(() => setQ(v), 350);
    return () => clearTimeout(t);
  }, [text, q]);
  return [text, setText, q];
}

const clientLabel = (cl) => (cl ? `${cl.number ? `${cl.number} · ` : ''}${displayName(cl.name)}${cl.restricted ? ' 🔒' : ''}` : '');
const nameOf = (r) => personName(r);
const userName = (u) => (u ? u.name : '—');

// --- the owner box (Clients profile panel + workspace) ------------------------------------------------------------
export function OwnerBox({ personId, darkMode, onChanged, testid = 'wk-owner' }) {
  const c = usePersonColors(darkMode);
  const [claim, setClaim] = useState(null);
  const [meta] = useMeta();
  const [pick, setPick] = useState('');
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    work().claim(personId).then((r) => setClaim(r.data)).catch((e) => setError(detailOf(e)));
  }, [personId]);
  useEffect(() => { setClaim(null); setChanging(false); setError(null); load(); }, [load]);

  const set = async (uid) => {
    setBusy(true); setError(null);
    try {
      const r = await work().setOwner(personId, uid, claim && claim.owner ? claim.owner.id : null);
      setClaim(r.data); setChanging(false);
      if (onChanged) onChanged(r.data);
    } catch (e) {
      setError(detailOf(e));
      if (e?.response?.status === 409) load();
    }
    setBusy(false);
  };
  if (!claim) return <div style={{ fontSize: fs(13), color: c.sub }} data-testid={testid}>{error || 'Loading owner…'}</div>;
  const owner = claim.owner;
  const me = meta && meta.me;
  return (
    <div data-testid={testid} style={{ fontSize: fs(13) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span data-testid={`${testid}-name`}>
          {owner ? <><strong>{owner.name}</strong>{claim.claimed_label ? <span style={{ color: c.sub }}> · since {claim.claimed_label}</span> : null}</>
            : <span style={{ color: c.sub }}>Unclaimed{claim.inactive_owner ? ` (was ${claim.inactive_owner.name} — inactive)` : ''}</span>}
        </span>
        {!owner && me && <button style={btn(c, true, busy)} disabled={busy} onClick={() => set(me.id)} data-testid={`${testid}-claim`}>Claim</button>}
        {owner && me && owner.id !== me.id && !changing && (
          <button style={btn(c, false, busy)} disabled={busy} onClick={() => set(me.id)} data-testid={`${testid}-claim-me`}>Claim for me</button>
        )}
        {!changing && <button style={btn(c, false, busy)} disabled={busy} onClick={() => { setPick(''); setChanging(true); }}
          data-testid={`${testid}-change`}>{owner ? 'Change owner' : 'Assign to…'}</button>}
        {owner && !changing && <button style={btn(c, false, busy)} disabled={busy} data-testid={`${testid}-remove`}
          onClick={() => { if (window.confirm(`Remove ${owner.name} as the owner of this client?`)) set(null); }}>Remove</button>}
      </div>
      {changing && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          <select aria-label="New owner" value={pick} onChange={(e) => setPick(e.target.value)} style={inputStyle(c, 'auto')} data-testid={`${testid}-pick`}>
            <option value="">Choose an employee…</option>
            {((meta && meta.users) || []).filter((u) => !owner || u.id !== owner.id).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <button style={btn(c, true, !pick || busy)} disabled={!pick || busy} onClick={() => set(Number(pick))} data-testid={`${testid}-save`}>Save</button>
          <button style={btn(c)} onClick={() => setChanging(false)}>Cancel</button>
        </div>
      )}
      <Err msg={error} />
    </div>
  );
}

// --- the workspace ------------------------------------------------------------------------------------------------
function NotesBox({ c, w, personId, onSaved }) {
  const season = w.season;
  const [text, setText] = useState(w.notes.body || '');
  const [token, setToken] = useState(w.notes.updated_at);
  const [savedInfo, setSavedInfo] = useState(w.notes);
  const [conflict, setConflict] = useState(null);
  const [kept, setKept] = useState(null);           // the user's own text after "Load latest"
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState(null);
  const dirty = text !== (savedInfo.body || '');
  const max = w.notes.max || 20000;
  const count = Array.from(text).length;

  useEffect(() => {                                  // leaving with unsaved notes asks first
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const r = await work().saveNotes(personId, season, text, token);
      setToken(r.data.updated_at); setSavedInfo(r.data); setConflict(null);
      if (onSaved) onSaved(r.data);
    } catch (e) {
      if (e?.response?.status === 409) setConflict(detailOf(e)); else setError(detailOf(e));
    }
    setBusy(false);
  };
  const loadLatest = async () => {
    setError(null);
    try {
      const r = await work().workspace(personId, season);
      setKept(text);
      setText(r.data.notes.body || ''); setToken(r.data.notes.updated_at); setSavedInfo(r.data.notes); setConflict(null);
    } catch (e) { setError(detailOf(e)); }
  };
  const showVersions = async () => {
    if (versions) { setVersions(null); return; }
    try { const r = await work().noteVersions(personId, season); setVersions(r.data.versions); } catch (e) { setError(detailOf(e)); }
  };

  return (
    <div style={box(c)} data-testid="wk-notes">
      <H c={c} right={w.is_admin ? <button style={{ ...btn(c), padding: '3px 8px', fontSize: fs(12) }} onClick={showVersions} data-testid="wk-notes-versions">{versions ? 'Hide versions' : 'Versions'}</button> : null}>
        Notes · {season}
      </H>
      <textarea value={text} onChange={(e) => setText(e.target.value)} readOnly={w.read_only} data-testid="wk-notes-text"
        aria-label="Notes" placeholder={w.read_only ? 'No notes for this season' : 'Notes for this client and season…'}
        style={{ ...inputStyle(c), minHeight: 260, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.45 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
        {!w.read_only && <button style={btn(c, true, !dirty || busy || count > max)} disabled={!dirty || busy || count > max} onClick={save}
          data-testid="wk-notes-save">{busy ? 'Saving…' : 'Save notes'}</button>}
        <span className="wk-sub" style={{ color: count > max ? '#c0392b' : c.sub, fontSize: fs(12) }}>{count.toLocaleString()} / {max.toLocaleString()}</span>
        {dirty && !w.read_only && <span style={{ fontSize: fs(12), color: '#b9770e' }} data-testid="wk-notes-dirty">unsaved changes</span>}
        <span style={{ flex: 1 }} />
        {savedInfo.updated_label && <span style={{ fontSize: fs(12), color: c.sub }} data-testid="wk-notes-saved">Saved {savedInfo.updated_label}{savedInfo.updated_by ? ` by ${savedInfo.updated_by.name}` : ''}</span>}
      </div>
      {conflict && (
        <div role="alert" data-testid="wk-notes-conflict" style={{ background: '#7f1d1d', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: fs(13), marginTop: 8 }}>
          {conflict}
          <div style={{ marginTop: 6 }}>
            <button style={{ ...btn(c), padding: '3px 8px' }} onClick={loadLatest} data-testid="wk-notes-load-latest">Load latest (keep my text below)</button>
          </div>
        </div>
      )}
      {kept !== null && (
        <div style={{ marginTop: 8 }} data-testid="wk-notes-kept">
          <div style={{ fontSize: fs(12), color: c.sub, marginBottom: 4 }}>Your text from before — copy what you need into the notes above:</div>
          <textarea readOnly value={kept} style={{ ...inputStyle(c), minHeight: 90, fontFamily: 'inherit' }} aria-label="Your earlier text" />
          <button style={{ ...btn(c), padding: '3px 8px', marginTop: 4 }} onClick={() => setKept(null)}>Dismiss</button>
        </div>
      )}
      <Err msg={error} />
      {versions && (
        <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto' }} data-testid="wk-notes-version-list">
          {versions.length === 0 && <div style={{ fontSize: fs(13), color: c.sub }}>No saved versions.</div>}
          {versions.map((v) => (
            <details key={v.id} style={{ fontSize: fs(13), borderTop: `1px solid ${c.border}`, padding: '4px 0' }}>
              <summary>{v.at_label} · {userName(v.by)} · {v.characters.toLocaleString()} characters</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: '4px 0 0' }}>{v.body}</pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function TodoBox({ c, w, personId, todos, setTodos }) {
  const [text, setText] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true); setError(null);
    try { const r = await fn(); setTodos(r.data.todos); return true; } catch (e) { setError(detailOf(e)); return false; } finally { setBusy(false); }
  };
  const add = async () => { if (text.trim() && await run(() => work().addTodo(personId, w.season, text))) setText(''); };
  const move = (i, d) => {
    const ids = todos.map((t) => t.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    run(() => work().orderTodos(personId, w.season, ids));
  };
  const ro = w.read_only;
  const open = todos.filter((t) => !t.done).length;
  return (
    <div style={box(c)} data-testid="wk-todos">
      <H c={c}>To-do · {open} open of {todos.length}</H>
      {!ro && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a to-do…" maxLength={500} aria-label="New to-do"
            onKeyDown={(e) => { if (e.key === 'Enter') add(); }} style={inputStyle(c)} data-testid="wk-todo-input" />
          <button style={btn(c, true, busy || !text.trim())} disabled={busy || !text.trim()} onClick={add} data-testid="wk-todo-add">Add</button>
        </div>
      )}
      {todos.length === 0 && <div style={{ fontSize: fs(13), color: c.sub }}>Nothing on the list.</div>}
      {todos.map((t, i) => (
        <div key={t.id} data-testid="wk-todo" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', borderTop: i ? `1px solid ${c.border}` : 'none' }}>
          <input type="checkbox" checked={t.done} disabled={ro || busy} aria-label={`Done: ${t.text}`}
            onChange={(e) => run(() => work().setTodoDone(t.id, e.target.checked))} style={{ marginTop: 3 }} />
          <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            <div style={{ fontSize: fs(13), textDecoration: t.done ? 'line-through' : 'none', color: t.done ? c.sub : c.text }}>{t.text}</div>
            {t.done && <div style={{ fontSize: fs(11), color: c.sub }}>✓ {userName(t.done_by)} · {t.done_label}</div>}
          </div>
          {!ro && (
            <span style={{ display: 'inline-flex', gap: 2 }}>
              <button style={{ ...btn(c, false, i === 0), padding: '1px 6px' }} disabled={i === 0 || busy} onClick={() => move(i, -1)} aria-label="Move up">▲</button>
              <button style={{ ...btn(c, false, i === todos.length - 1), padding: '1px 6px' }} disabled={i === todos.length - 1 || busy} onClick={() => move(i, 1)} aria-label="Move down">▼</button>
              <button style={{ ...btn(c), padding: '1px 6px' }} disabled={busy} aria-label="Delete to-do"
                onClick={() => { if (window.confirm('Delete this to-do?')) run(() => work().deleteTodo(t.id)); }}>✕</button>
            </span>
          )}
        </div>
      ))}
      <Err msg={error} />
    </div>
  );
}

function AlertsBox({ c, w, personId, alerts, setAlerts }) {
  const types = (w.alert_types || []).filter((t) => t.is_active);
  const [typeId, setTypeId] = useState('');
  const [due, setDue] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true); setError(null);
    try { const r = await fn(); setAlerts(r.data.alerts); return true; } catch (e) { setError(detailOf(e)); return false; } finally { setBusy(false); }
  };
  const add = async () => {
    if (await run(() => work().addAlert(personId, w.season, Number(typeId), due, note))) { setDue(''); setNote(''); }
  };
  const ro = w.read_only;
  return (
    <div style={box(c)} data-testid="wk-alerts">
      <H c={c}>Follow-up alerts · {w.season}</H>
      {!ro && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <select aria-label="Alert type" value={typeId} onChange={(e) => setTypeId(e.target.value)} style={inputStyle(c, 'auto')} data-testid="wk-alert-type">
            <option value="">Type…</option>
            {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input type="date" aria-label="Due date" value={due} onChange={(e) => setDue(e.target.value)} style={inputStyle(c, 'auto')} data-testid="wk-alert-due" />
          <input aria-label="Short note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Short note (optional)"
            style={{ ...inputStyle(c, 'auto'), flex: '1 1 200px' }} data-testid="wk-alert-note" />
          <button style={btn(c, true, busy || !typeId || !due)} disabled={busy || !typeId || !due} onClick={add} data-testid="wk-alert-add">Add alert</button>
        </div>
      )}
      {alerts.length === 0 && <div style={{ fontSize: fs(13), color: c.sub }}>No follow-up alerts this season.</div>}
      {alerts.map((a) => (
        <div key={a.id} data-testid="wk-alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: `1px solid ${c.border}`, flexWrap: 'wrap' }}>
          <BandChip band={a.band} days={a.days} completed={a.completed} />
          <strong style={{ fontSize: fs(13) }}>{a.type}</strong>
          <span style={{ fontSize: fs(13) }}>due {a.due_label}</span>
          {a.note && <span style={{ fontSize: fs(13), color: c.sub, overflowWrap: 'anywhere' }}>— {a.note}</span>}
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: fs(11), color: c.sub }}>
            {a.completed ? `done by ${userName(a.completed_by)} · ${a.completed_label}` : `added by ${userName(a.created_by)}`}
          </span>
          {!ro && (a.completed
            ? <button style={{ ...btn(c), padding: '2px 8px' }} disabled={busy} onClick={() => run(() => work().uncompleteAlert(a.id))}>Undo</button>
            : <button style={{ ...btn(c, true), padding: '2px 8px' }} disabled={busy} onClick={() => run(() => work().completeAlert(a.id))} data-testid="wk-alert-complete">Complete</button>)}
          {w.is_admin && <button style={{ ...btn(c), padding: '2px 8px' }} disabled={busy} aria-label="Delete alert"
            onClick={() => { if (window.confirm('Delete this alert? (admins only — completing it is usually better)')) run(() => work().deleteAlert(a.id)); }}>Delete</button>}
        </div>
      ))}
      <Err msg={error} />
    </div>
  );
}

function FinalizeBox({ c, w, personId, onChange }) {
  const [fin, setFin] = useState(w.finalized);
  const [events, setEvents] = useState(w.finalize_events || []);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true); setError(null);
    try { const r = await fn(); setFin(r.data.finalized); setEvents(r.data.finalize_events); if (onChange) onChange(); } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  return (
    <div style={box(c)} data-testid="wk-finalize">
      <H c={c}>Finalize · {w.season}</H>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: fs(13) }}>
        {fin
          ? <span data-testid="wk-finalized"><strong style={{ color: '#15803d' }}>✓ Finalized</strong> {fin.at_label} by {userName(fin.by)}{fin.owner ? ` · owner ${fin.owner.name}` : ' · unclaimed'}</span>
          : <span style={{ color: c.sub }}>Not finalized for {w.season}.</span>}
        {!w.read_only && (fin
          ? <button style={btn(c, false, busy)} disabled={busy} onClick={() => { if (window.confirm(`Reopen ${w.season} for this client?`)) run(() => work().reopen(personId, w.season)); }} data-testid="wk-reopen">Reopen</button>
          : <button style={btn(c, true, busy)} disabled={busy} onClick={() => { if (window.confirm(`Finalize ${w.season} for this client?`)) run(() => work().finalize(personId, w.season)); }} data-testid="wk-finalize-btn">Finalize</button>)}
      </div>
      {events.length > 0 && (
        <div style={{ marginTop: 8, fontSize: fs(12), color: c.sub }}>
          {events.map((e, i) => <div key={i}>{e.action === 'finalize' ? 'Finalized' : 'Reopened'} {e.at_label} by {userName(e.by)}</div>)}
        </div>
      )}
      <Err msg={error} />
    </div>
  );
}

export function Workspace({ personId, season, darkMode, onSeason, onBack, onOpenClient, onMoved }) {
  const c = usePersonColors(darkMode);
  const [w, setW] = useState(null);
  const [error, setError] = useState(null);
  const [todos, setTodos] = useState([]);
  const [finTick, setFinTick] = useState(0);   // 5.47.0: the renewal bonus panel re-reads after Finalize / Reopen
  const [alerts, setAlerts] = useState([]);
  const [showEvents, setShowEvents] = useState(false);
  // 5.46.0: YEAR TABS (5 most recent plan years + Archive) replace the Season dropdown and drive notes / to-dos / alerts;
  // with no year in the address bar the tab is the year the client has coverage now (2026 until Jan 1).
  const [years, setYears] = useState(null);
  useEffect(() => {
    let alive = true;
    setYears(null);
    healthsherpa.coverage.years(personId).then((r) => { if (alive) setYears(r.data); })
      .catch(() => { if (alive) setYears({ tabs: [], archive: [], default: null }); });
    return () => { alive = false; };
  }, [personId]);
  const year = season || (years ? years.default : null);
  const [cov, covError, covReload] = useYearView(personId, year && years ? year : null, 0);
  useEffect(() => {
    if (!years) return undefined;
    let alive = true;
    setW(null); setError(null);
    work().workspace(personId, year).then((r) => { if (!alive) return; setW(r.data); setTodos(r.data.todos); setAlerts(r.data.alerts); })
      .catch((e) => {
        if (!alive) return;
        const moved = e?.response?.data?.moved_to;
        if (moved && onMoved) { onMoved(moved); return; }
        setError(e?.response?.status === 404 ? 'This client is not available.' : detailOf(e));
      });
    return () => { alive = false; };
  }, [personId, year, years]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <div style={{ padding: 18 }}>{onBack && <button style={btn(c)} onClick={onBack}>◀ Back</button>}<Err msg={error} /></div>;
  if (!w) return <div style={{ padding: 18, color: c.sub }}>Loading…</div>;
  const p = w.person;
  const cl = p.client;
  return (
    <div style={{ padding: 18, display: 'grid', gap: 14 }} data-testid="wk-workspace" data-pid={personId}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {onBack && <button style={btn(c)} onClick={onBack} data-testid="wk-back">◀ My Clients</button>}
        <div style={{ fontSize: fs(19), fontWeight: 700 }} data-testid="wk-name">{nameOf(p)}</div>
        {cl && <span style={{ fontSize: fs(13), color: c.sub }}>{clientLabel(cl)}</span>}
        {cl && onOpenClient && <button style={btn(c)} onClick={() => onOpenClient(cl.id)}>Open InsureDocs client</button>}
        <span style={{ flex: 1 }} />
        <YearTabs c={c} years={years} value={w.season} onChange={(y) => onSeason(y)} />
      </div>
      {w.read_only && <div role="status" data-testid="wk-readonly" style={{ fontSize: fs(13), background: c.panel, border: `1px solid ${c.border}`, borderRadius: 6, padding: '6px 10px' }}>
        {w.season > w.default_season ? null : `Notes, to-dos and alerts for ${w.season} are read-only — the current work season is ${w.default_season}.`}{' '}
        <button style={{ ...btn(c), padding: '1px 8px' }} onClick={() => onSeason(w.default_season)} data-testid="wk-go-season">Go to {w.default_season}</button></div>}
      <CoverageSummary personId={personId} year={w.season} darkMode={darkMode} data={cov} error={covError} reload={covReload} isAdmin={!!w.is_admin} />
      <div style={box(c)}>
        <H c={c} right={<button style={{ ...btn(c), padding: '3px 8px', fontSize: fs(12) }} onClick={() => setShowEvents(!showEvents)}>{showEvents ? 'Hide history' : 'Owner history'}</button>}>Owner</H>
        <OwnerBox personId={personId} darkMode={darkMode} />
        {showEvents && (
          <div style={{ marginTop: 8, fontSize: fs(12), color: c.sub }} data-testid="wk-claim-events">
            {(w.claim_events || []).length === 0 && <div>No owner changes yet.</div>}
            {(w.claim_events || []).map((e) => <div key={e.id}>{e.at_label} · {e.reason} · {userName(e.from)} → {userName(e.to)} (by {userName(e.by)})</div>)}
          </div>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)', gap: 14, alignItems: 'start' }}>
        <NotesBox key={`${personId}-${w.season}`} c={c} w={w} personId={personId} />
        <TodoBox c={c} w={w} personId={personId} todos={todos} setTodos={setTodos} />
      </div>
      <AlertsBox c={c} w={w} personId={personId} alerts={alerts} setAlerts={setAlerts} />
      <FinalizeBox key={`f-${personId}-${w.season}`} c={c} w={w} personId={personId} onChange={() => setFinTick((n) => n + 1)} />
      {w.season === 2027 && <RenewalBonusPanel c={c} personId={personId} year={2027} refreshKey={finTick} />}{/* 5.47.0 */}
      <MoreDetails darkMode={darkMode} data={cov} />
      <ComparePanel personId={personId} darkMode={darkMode} years={years} current={w.season} />
    </div>
  );
}

// --- My Clients ----------------------------------------------------------------------------------------------------
export function MyClientsTab({ darkMode, onOpenClient }) {
  const c = usePersonColors(darkMode);
  const [params, go] = useWorkUrl('mine');
  const [meta, metaError] = useMeta();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [privacy, togglePrivacy] = usePrivacy();
  const season = params.season ? Number(params.season) : (meta ? meta.default_season : null);
  const person = params.person ? Number(params.person) : null;

  useEffect(() => {
    if (person || !season) return undefined;
    let alive = true;
    work().mine(season).then((r) => { if (alive) { setData(r.data); setError(null); } }).catch((e) => { if (alive) setError(detailOf(e)); });
    return () => { alive = false; };
  }, [person, season]);

  if (person) {
    return (
      <Workspace personId={person} season={params.season ? Number(params.season) : null} darkMode={darkMode} onOpenClient={onOpenClient}
        onSeason={(s) => go({ season: String(s) })} onBack={() => go({ person: '' })}
        onMoved={(id) => go({ person: String(id) }, { replace: true })} />
    );
  }
  const rows = (data && data.rows) || [];
  const vars = { '--wk-border': c.border, '--wk-sub': c.sub, '--wk-hover': darkMode ? '#2c3138' : '#eef2f7' };
  const pii = privacy ? 'hs-pii' : undefined;
  return (
    <div className="hs-wk" style={{ padding: 18, display: 'grid', gap: 14, ...vars }} data-testid="wk-mine">
      <style>{css}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: fs(16), fontWeight: 700 }}>My Clients</div>
        {meta && (
          <select aria-label="Season" value={season || ''} onChange={(e) => go({ season: e.target.value })} style={inputStyle(c, 'auto')} data-testid="wk-mine-season">
            {meta.seasons.map((s) => <option key={s} value={s}>{s}{s === meta.default_season ? ' (current)' : ''}</option>)}
          </select>
        )}
        <span style={{ fontSize: fs(13), color: c.sub }}>{data ? `${data.total} claimed` : ''}</span>
        <span style={{ flex: 1 }} />
        <PrivacyButton c={c} on={privacy} onToggle={togglePrivacy} />
      </div>
      <Err msg={metaError || error} />
      {data && rows.length === 0 && (
        <div style={{ ...box(c), fontSize: fs(13), color: c.sub }}>You haven't claimed any clients yet — open a client on the Clients tab and press Claim.</div>
      )}
      {rows.length > 0 && (
        <div style={{ ...box(c), padding: 0, overflowX: 'auto' }}>
          <table data-testid="wk-mine-table">
            <thead><tr><th>Client</th><th>InsureDocs</th><th>Coverage now</th><th>Status</th><th>Paid through</th><th>Net premium</th><th>Next follow-up</th><th>Open alerts</th><th>To-do</th><th>{season}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.person_id} className="wk-click" tabIndex={0} data-testid="wk-mine-row" data-pid={r.person_id}
                  onClick={() => go({ person: String(r.person_id), season: '' })}
                  onKeyDown={(e) => { if (e.key === 'Enter') go({ person: String(r.person_id), season: '' }); }}>
                  <td className={pii} title={privacy ? undefined : nameOf(r)}><strong>{nameOf(r)}</strong></td>
                  <td className={pii} title={privacy ? undefined : clientLabel(r.client)}>{r.client ? clientLabel(r.client) : <span className="wk-sub">not assigned</span>}</td>
                  {/* 5.46.0: every claimed client's current coverage, even with no notes / to-dos / alerts */}
                  <td data-testid="wk-mine-coverage">{r.coverage && r.coverage.plan
                    ? <><strong>{r.coverage.carrier}</strong><div className="wk-sub">{r.coverage.plan}{r.coverage.metal ? ` · ${r.coverage.metal}` : ''} · {r.coverage.plan_year}</div></>
                    : <span className="wk-sub">—</span>}
                    {r.coverage && <Flags flags={r.coverage.flags} testid="wk-mine-flags" />}</td>
                  <td>{(r.coverage && r.coverage.status) || <span className="wk-sub">—</span>}</td>
                  <td data-testid="wk-mine-paid">{(r.coverage && r.coverage.paid_through) || <span className="wk-sub">—</span>}</td>
                  <td>{r.coverage && r.coverage.net_premium_cents !== null && r.coverage.net_premium_cents !== undefined ? usd(r.coverage.net_premium_cents) : <span className="wk-sub">—</span>}</td>
                  <td>{r.next_due ? <><BandChip band={r.next_due.band} days={r.next_due.days} /> <span className="wk-sub">{r.next_due.due_label}</span></> : <span className="wk-sub">—</span>}</td>
                  <td>{r.open_alerts || <span className="wk-sub">0</span>}</td>
                  <td>{r.todos ? `${r.todos_open} open / ${r.todos}` : <span className="wk-sub">—</span>}</td>
                  <td>{r.finalized ? <strong style={{ color: '#15803d' }}>✓ Finalized</strong> : <span className="wk-sub">open</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Follow-ups ------------------------------------------------------------------------------------------------------
export function FollowupsTab({ darkMode, onOpenWorkspace }) {
  const c = usePersonColors(darkMode);
  const [params, go] = useWorkUrl('followups');
  const [meta, metaError] = useMeta();
  const [text, setText, q] = useSearch();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [privacy, togglePrivacy] = usePrivacy();
  const scope = params.scope === 'mine' ? 'mine' : 'all';
  const show = params.show === 'completed' ? 'completed' : 'open';
  const key = JSON.stringify({ scope, show, type: params.type || '', q });
  useEffect(() => {
    let alive = true;
    const p = JSON.parse(key);
    work().followups({ scope: p.scope, show: p.show, type_id: p.type ? Number(p.type) : null, q: p.q })
      .then((r) => { if (alive) { setData(r.data); setError(null); } }).catch((e) => { if (alive) setError(detailOf(e)); });
    return () => { alive = false; };
  }, [key, reload]);
  const act = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); setReload((k) => k + 1); } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  const rows = (data && data.rows) || [];
  const counts = (data && data.counts) || {};
  const vars = { '--wk-border': c.border, '--wk-sub': c.sub, '--wk-hover': darkMode ? '#2c3138' : '#eef2f7' };
  const pii = privacy ? 'hs-pii' : undefined;
  const seg = (v, label) => (
    <button key={v} onClick={() => go({ scope: v === 'all' ? '' : v })} aria-pressed={scope === v} data-testid={`wk-fu-scope-${v}`}
      style={{ ...btn(c, scope === v), borderRadius: 0 }}>{label}</button>
  );
  return (
    <div className="hs-wk" style={{ padding: 18, display: 'grid', gap: 14, ...vars }} data-testid="wk-followups">
      <style>{css}</style>
      <div style={{ ...box(c), display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', borderRadius: 5, overflow: 'hidden' }}>{seg('mine', 'Mine')}{seg('all', 'All')}</span>
        <select aria-label="Alert type" value={params.type || ''} onChange={(e) => go({ type: e.target.value })} style={inputStyle(c, 'auto')} data-testid="wk-fu-type">
          <option value="">All types</option>
          {((meta && meta.alert_types) || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select aria-label="Show" value={show} onChange={(e) => go({ show: e.target.value === 'open' ? '' : e.target.value })} style={inputStyle(c, 'auto')} data-testid="wk-fu-show">
          <option value="open">Open (+ completed in the last {(meta && meta.bands && meta.bands.completed_days) || 7} days)</option>
          <option value="completed">Completed</option>
        </select>
        <input type="search" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search follow-ups" data-testid="wk-fu-search"
          placeholder="Search a client, owner, type or note" style={{ ...inputStyle(c, 'auto'), flex: '1 1 260px' }} />
        <PrivacyButton c={c} on={privacy} onToggle={togglePrivacy} />
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: fs(13) }} data-testid="wk-fu-counts">
        <strong>{data ? `${data.total} alert${data.total === 1 ? '' : 's'}` : 'Loading…'}</strong>
        {['overdue', 'red', 'yellow', 'orange', 'done'].filter((b) => counts[b]).map((b) => (
          <span key={b} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: bandStyle(b).bg, display: 'inline-block' }} />{bandStyle(b).label}: {counts[b]}
          </span>
        ))}
      </div>
      <Err msg={metaError || error} />
      {data && rows.length === 0 && <div style={{ ...box(c), fontSize: fs(13), color: c.sub }}>No follow-ups here.</div>}
      {rows.length > 0 && (
        <div style={{ ...box(c), padding: 0, overflowX: 'auto' }}>
          <table data-testid="wk-fu-table">
            <thead><tr><th>Due</th><th>Client</th><th>Type</th><th>Note</th><th>Owner</th><th>Season</th><th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid="wk-fu-row" data-band={r.band} style={{ boxShadow: `inset 4px 0 0 ${bandStyle(r.band).bg}` }}>
                  <td style={{ whiteSpace: 'nowrap' }}><BandChip band={r.band} days={r.days} completed={r.completed} /><div className="wk-sub">{r.due_label}</div></td>
                  <td className={pii} title={privacy ? undefined : nameOf(r)}>
                    <button onClick={() => onOpenWorkspace(r.person_id, r.season)} data-testid="wk-fu-open"
                      style={{ background: 'none', border: 'none', padding: 0, color: c.link, cursor: 'pointer', fontSize: fs(13), fontWeight: 600, textAlign: 'left' }}>{nameOf(r)}</button>
                    {r.client && <div className="wk-sub">{clientLabel(r.client)}</div>}
                  </td>
                  <td>{r.type}</td>
                  <td className={pii} style={{ overflowWrap: 'anywhere' }}>{r.note || <span className="wk-sub">—</span>}</td>
                  <td>{r.owner ? r.owner.name : <span className="wk-sub">unclaimed</span>}</td>
                  <td>{r.season}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.completed
                      ? <button style={{ ...btn(c), padding: '2px 8px' }} disabled={busy} onClick={() => act(() => work().uncompleteAlert(r.id))} title={`Done by ${userName(r.completed_by)} · ${r.completed_label}`}>Undo</button>
                      : <button style={{ ...btn(c, true), padding: '2px 8px' }} disabled={busy} onClick={() => act(() => work().completeAlert(r.id))} data-testid="wk-fu-complete">Complete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// --- Finalized ---------------------------------------------------------------------------------------------------------
export function FinalizedTab({ darkMode, onOpenWorkspace }) {
  const c = usePersonColors(darkMode);
  const [params, go] = useWorkUrl('finalized');
  const [text, setText, q] = useSearch();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [privacy, togglePrivacy] = usePrivacy();
  const key = JSON.stringify({ ...params, q });
  const firstQ = useRef(q);
  useEffect(() => {                                   // a new search goes back to page 1
    if (firstQ.current !== q && params.page) go({ page: '' }, { replace: true });
    firstQ.current = q;
  }, [q]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    const p = JSON.parse(key);
    work().finalized({ q: p.q, season: p.season ? Number(p.season) : null, owner_id: p.owner ? Number(p.owner) : null,
      date_from: p.from || null, date_to: p.to || null, page: Number(p.page) || 1 })
      .then((r) => { if (alive) { setData(r.data); setError(null); } }).catch((e) => { if (alive) setError(detailOf(e)); });
    return () => { alive = false; };
  }, [key]);
  const rows = (data && data.rows) || [];
  const vars = { '--wk-border': c.border, '--wk-sub': c.sub, '--wk-hover': darkMode ? '#2c3138' : '#eef2f7' };
  const pii = privacy ? 'hs-pii' : undefined;
  const set = (patch) => go({ ...patch, page: '' });
  return (
    <div className="hs-wk" style={{ padding: 18, display: 'grid', gap: 14, ...vars }} data-testid="wk-finalized-tab">
      <style>{css}</style>
      <div style={{ ...box(c), display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <input type="search" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search finalized clients" data-testid="wk-fin-search"
          placeholder="Search a client name or InsureDocs client" style={{ ...inputStyle(c, 'auto'), flex: '1 1 260px' }} />
        <select aria-label="Season" value={params.season || ''} onChange={(e) => set({ season: e.target.value })} style={inputStyle(c, 'auto')} data-testid="wk-fin-season">
          <option value="">All seasons</option>
          {((data && data.seasons) || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select aria-label="Employee" value={params.owner || ''} onChange={(e) => set({ owner: e.target.value })} style={inputStyle(c, 'auto')} data-testid="wk-fin-owner">
          <option value="">All employees</option>
          {((data && data.owners) || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <label style={{ fontSize: fs(13), display: 'inline-flex', gap: 4, alignItems: 'center' }}>From
          <input type="date" value={params.from || ''} onChange={(e) => set({ from: e.target.value })} style={inputStyle(c, 'auto')} aria-label="Finalized from" /></label>
        <label style={{ fontSize: fs(13), display: 'inline-flex', gap: 4, alignItems: 'center' }}>To
          <input type="date" value={params.to || ''} onChange={(e) => set({ to: e.target.value })} style={inputStyle(c, 'auto')} aria-label="Finalized to" /></label>
        <PrivacyButton c={c} on={privacy} onToggle={togglePrivacy} />
      </div>
      <div style={{ fontSize: fs(13) }}><strong>{data ? `${data.total} finalized` : 'Loading…'}</strong></div>
      <Err msg={error} />
      {rows.length > 0 && (
        <div style={{ ...box(c), padding: 0, overflowX: 'auto' }}>
          <table data-testid="wk-fin-table">
            <thead><tr><th>Client</th><th>InsureDocs</th><th>Season</th><th>Finalized</th><th>By</th><th>Owner at finalize</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.person_id}-${r.season}`} className="wk-click" tabIndex={0} data-testid="wk-fin-row"
                  onClick={() => onOpenWorkspace(r.person_id, r.season)} onKeyDown={(e) => { if (e.key === 'Enter') onOpenWorkspace(r.person_id, r.season); }}>
                  <td className={pii} title={privacy ? undefined : nameOf(r)}><strong>{nameOf(r)}</strong></td>
                  <td className={pii}>{r.client ? clientLabel(r.client) : <span className="wk-sub">—</span>}</td>
                  <td>{r.season}</td>
                  <td>{r.finalized_label}</td>
                  <td>{userName(r.by)}</td>
                  <td>{r.owner ? r.owner.name : <span className="wk-sub">unclaimed</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: fs(13) }}>
          <button style={btn(c, false, data.page <= 1)} disabled={data.page <= 1} onClick={() => go({ page: String(data.page - 1) })}>‹ Prev</button>
          <span>Page {data.page} of {data.pages}</span>
          <button style={btn(c, false, data.page >= data.pages)} disabled={data.page >= data.pages} onClick={() => go({ page: String(data.page + 1) })}>Next ›</button>
        </div>
      )}
    </div>
  );
}

// --- Settings: alert types (admin) ------------------------------------------------------------------------------
export function AlertTypesCard({ darkMode }) {
  const c = usePersonColors(darkMode);
  const [types, setTypes] = useState(null);
  const [name, setName] = useState('');
  const [edit, setEdit] = useState({});
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { work().alertTypes(true).then((r) => setTypes(r.data.types)).catch((e) => setError(detailOf(e))); }, []);
  const run = async (fn) => {
    setBusy(true); setError(null);
    try { const r = await fn(); setTypes(r.data.types); return true; } catch (e) { setError(detailOf(e)); return false; } finally { setBusy(false); }
  };
  const sorted = useMemo(() => (types || []).slice().sort((a, b) => Number(b.is_active) - Number(a.is_active)), [types]);
  if (!types) return <div style={{ fontSize: fs(13), color: c.sub }}>{error || 'Loading…'}</div>;
  return (
    <div data-testid="wk-alert-types">
      <div style={{ fontSize: fs(12), color: c.sub, marginBottom: 8 }}>Types for follow-up alerts. A type that has been used can be renamed or retired, not deleted.</div>
      {sorted.map((t) => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderTop: `1px solid ${c.border}`, flexWrap: 'wrap' }} data-testid="wk-alert-type-row">
          {edit[t.id] !== undefined
            ? <>
              <input value={edit[t.id]} maxLength={60} onChange={(e) => setEdit({ ...edit, [t.id]: e.target.value })} style={inputStyle(c, 220)} aria-label={`Rename ${t.name}`} />
              <button style={btn(c, true, busy)} disabled={busy} onClick={async () => { if (await run(() => work().editAlertType(t.id, { name: edit[t.id] }))) { const n = { ...edit }; delete n[t.id]; setEdit(n); } }}>Save</button>
              <button style={btn(c)} onClick={() => { const n = { ...edit }; delete n[t.id]; setEdit(n); }}>Cancel</button>
            </>
            : <span style={{ fontSize: fs(13), flex: 1, color: t.is_active ? c.text : c.sub }}>{t.name}{t.is_active ? '' : ' (retired)'}{t.used ? <span style={{ color: c.sub }}> · in use</span> : null}</span>}
          {edit[t.id] === undefined && <button style={{ ...btn(c), padding: '2px 8px' }} onClick={() => setEdit({ ...edit, [t.id]: t.name })}>Rename</button>}
          {edit[t.id] === undefined && <button style={{ ...btn(c), padding: '2px 8px' }} disabled={busy}
            onClick={() => run(() => work().editAlertType(t.id, { is_active: !t.is_active }))}>{t.is_active ? 'Retire' : 'Restore'}</button>}
          {edit[t.id] === undefined && !t.used && <button style={{ ...btn(c), padding: '2px 8px' }} disabled={busy}
            onClick={() => { if (window.confirm(`Delete the unused type "${t.name}"?`)) run(() => work().deleteAlertType(t.id)); }}>Delete</button>}
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="New type name" style={inputStyle(c, 220)} aria-label="New alert type"
          data-testid="wk-alert-type-new" />
        <button style={btn(c, true, busy || !name.trim())} disabled={busy || !name.trim()} data-testid="wk-alert-type-add"
          onClick={async () => { if (await run(() => work().addAlertType(name))) setName(''); }}>Add type</button>
      </div>
      <Err msg={error} />
    </div>
  );
}
